// ============================================================================
// INVOICES HOOK — useSupabaseInvoices
// ============================================================================
// Table:   invoices
// Follows: useSupabase.js conventions (requireSession, formatSupabaseError,
//          normalizeId, camelCase ↔ snake_case mapping)
//
// VISIBILITY / RLS RULES:
//   SELECT — admin + supervisor (users cannot see invoices)
//   INSERT — admin + supervisor
//   UPDATE — admin + supervisor (only while status = 'draft')
//   DELETE — admin only (soft-delete: sets deleted_at)
//
// STATUS LIFECYCLE:
//   draft → issued → paid
//   Issued invoices are immutable (no field edits, only payment confirmation)
//
// INVOICE NUMBERING (Feature 1):
//   Format: YYYYMMSS (e.g. 20260201 = first invoice of Feb 2026)
//   Allocated atomically via Postgres RPC allocate_invoice_number(date).
//
// ACCOUNT NAME LEARNING (Feature 2):
//   account_name on invoices stores the payer bank account name.
//   IMPORTANT: account_name is a CONFIDENCE SIGNAL only.
//
// FEE REVISION TRANSPARENCY (Feature 3):
//   On issue, snapshots are frozen: total_fee_snapshot, invoice_percent_snapshot,
//   base_amount_planned, adjustment_amount, recalc_breakdown_json,
//   recalc_explanation_text. Issued invoices are never recalculated.
// ============================================================================

import { useState, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { formatSupabaseError, normalizeId } from './useSupabase';

// ---------------------------------------------------------------------------
// READ/WRITE SOURCES
// ---------------------------------------------------------------------------
// IMPORTANT:
// - All MUTATIONS must go to the base table: 'invoices'
// - All READ/REPORTING should come from the reporting view so NL
//   invoices can be converted to INR without modifying immutable rows.
//   View: public.v_invoices_reporting
const INVOICES_READ_SOURCE = 'v_invoices_reporting';
const INVOICES_WRITE_SOURCE = 'invoices';

// ============================================================================
// SESSION GUARD — identical to useSupabase.js
// ============================================================================
const requireSession = async () => {
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) {
    console.error('[requireSession] Session error:', sessionError);
    throw new Error('Authentication error: ' + sessionError.message);
  }
  if (!session || !session.user) {
    console.error('[requireSession] No active Supabase session');
    throw new Error(
      'Please sign in to perform this action. ' +
      'Note: The "switch user" feature only changes the UI view — ' +
      'it does not change your database permissions. ' +
      'You must sign in with your actual credentials.'
    );
  }
  return session.user.id;
};

// ============================================================================
// DB → APP FIELD MAPPING
// ============================================================================
const mapRow = (r) => ({
  id:             normalizeId(r.id),
  projectId:      normalizeId(r.project_id),
  stageKey:       r.stage_key          ?? null,
  invoiceNumber:  r.invoice_number     ?? '',
  invoiceDate:    r.invoice_date       ?? null,
  baseAmount:     r.base_amount        ?? 0,
  gstPercent:     r.gst_percent        ?? 0,
  tdsPercent:     r.tds_percent        ?? 0,
  grossAmount:    r.gross_amount       ?? 0,
  tdsAmount:      r.tds_amount         ?? 0,
  netAmount:      r.net_amount         ?? 0,
  status:         r.status             ?? 'draft',
  paidConfirmed:  r.paid_confirmed     ?? false,
  paidDate:       r.paid_date          ?? null,
  note:           r.note               ?? '',
  isManualOverride: r.is_manual_override ?? false,
  // Feature 2: Account Name
  accountName:    r.account_name       ?? '',
  // Feature 4: Bank payment detection FK
  paymentEventId: r.payment_event_id   ?? null,
  // Feature 3: Fee revision transparency + frozen snapshots
  basisMode:             r.basis_mode              ?? 'percent',
  feeRevisionId:         r.fee_revision_id         ? normalizeId(r.fee_revision_id) : null,
  totalFeeSnapshot:      r.total_fee_snapshot      != null ? Number(r.total_fee_snapshot) : null,
  invoicePercentSnapshot: r.invoice_percent_snapshot != null ? Number(r.invoice_percent_snapshot) : null,
  baseAmountPlanned:     r.base_amount_planned     != null ? Number(r.base_amount_planned) : null,
  adjustmentAmount:      Number(r.adjustment_amount) || 0,
  recalcBreakdownJson:   r.recalc_breakdown_json   ?? null,
  recalcExplanationText: r.recalc_explanation_text  ?? '',
  // Import source (e.g. 'tally_import')
  source:         r.source             ?? null,

  // FX reporting (optional fields from v_invoices_reporting)
  // If the view overrides base_amount/gross_amount/net_amount directly, these
  // may be null/undefined — that's fine.
  baseAmountInrDefault:   r.base_amount_inr_default  != null ? Number(r.base_amount_inr_default) : null,
  grossAmountInrDefault:  r.gross_amount_inr_default != null ? Number(r.gross_amount_inr_default) : null,
  netAmountInrDefault:    r.net_amount_inr_default   != null ? Number(r.net_amount_inr_default) : null,
  baseAmountEurOriginal:  r.base_amount_eur_original  != null ? Number(r.base_amount_eur_original) : null,
  grossAmountEurOriginal: r.gross_amount_eur_original != null ? Number(r.gross_amount_eur_original) : null,
  netAmountEurOriginal:   r.net_amount_eur_original   != null ? Number(r.net_amount_eur_original) : null,
  eurInrAvgRate:          r.eur_inr_avg_rate          != null ? Number(r.eur_inr_avg_rate) : null,
  fxMonth:                r.fx_month                  ?? null,
  // Audit
  createdBy:      r.created_by         ?? null,
  createdAt:      r.created_at         ?? null,
  updatedAt:      r.updated_at         ?? null,
  deletedAt:      r.deleted_at         ?? null,
  // Office location (NL / IN)
  officeLocation: r.office_location    ?? null,
  // Explicit invoice currency (EUR / INR)
  currency:       r.currency           ?? 'INR',
  // Tax regime (Feature B) + TDS optional (Feature C)
  taxRegime:        r.tax_regime         ?? null,
  customTaxPercent: r.custom_tax_percent != null ? Number(r.custom_tax_percent) : null,
  applyTds:         r.apply_tds !== false,  // default true if column missing or null
  // Invoice document type: 'tax' (Tax Invoice) or 'proforma' (Proforma Invoice)
  invoiceDocType:   r.invoice_doc_type    ?? 'tax',
  // Custom stage name override for PDF rendering
  customStageName:  r.custom_stage_name   ?? null,
  // Per-invoice SAC code override (falls back to client's SAC in the PDF)
  sacCode:          r.sac_code            ?? null,
  // Archive tracking (month-end finance workflow)
  pdfGeneratedAt:      r.pdf_generated_at       ?? null,
  pdfFilename:         r.pdf_filename            ?? null,
  archivedToStorage:   r.archived_to_storage     ?? false,
  archivedAt:          r.archived_at             ?? null,
  archivedBy:          r.archived_by             ?? null,
  sentToAccountant:    r.sent_to_accountant      ?? false,
  sentToAccountantAt:  r.sent_to_accountant_at   ?? null,
  sentToAccountantBy:  r.sent_to_accountant_by   ?? null,
  archiveBatchMonth:   r.archive_batch_month     ?? null,
  // Feature 4: Bank payment detection (enriched from joined data in fetchInvoices)
  paymentDetected: false,
  paymentEvent:    null,
});

// ============================================================================
// ENRICH INVOICE WITH BANK PAYMENT EVENT (from joined row)
// ============================================================================
const enrichWithPaymentEvent = (mapped, rawRow) => {
  const events = rawRow.bank_payment_events;
  if (!Array.isArray(events) || events.length === 0) return mapped;
  // Pick the best suggested event (highest confidence)
  const suggested = events
    .filter(e => e.status === 'suggested')
    .sort((a, b) => (b.suggested_confidence || 0) - (a.suggested_confidence || 0))[0];
  if (!suggested) return mapped;
  return {
    ...mapped,
    paymentDetected: true,
    paymentEvent: {
      id:                  suggested.id,
      amount:              Number(suggested.amount) || 0,
      txnDate:             suggested.txn_date ?? null,
      payerName:           suggested.payer_name ?? '',
      narration:           suggested.narration ?? '',
      suggestedInvoiceId:  suggested.suggested_invoice_id ? normalizeId(suggested.suggested_invoice_id) : null,
      suggestedConfidence: Number(suggested.suggested_confidence) || 0,
      suggestedReasons:    suggested.suggested_reasons ?? null,
      status:              suggested.status,
    },
  };
};

// ---------------------------------------------------------------------------
// RE-FETCH FROM REPORTING VIEW
// ---------------------------------------------------------------------------
// After a mutation on the base table, re-read the row(s) from the reporting
// view so that INR-converted amounts are used in the UI (NL EUR → INR).
// Falls back to the base-table data if the view query fails.
// ---------------------------------------------------------------------------
const refetchFromView = async (idValue) => {
  try {
    const { data, error } = await supabase
      .from(INVOICES_READ_SOURCE)
      .select('*')
      .eq('id', idValue)
      .is('deleted_at', null)
      .maybeSingle();
    if (error) {
      console.warn('[INVOICES] refetchFromView error:', error.message);
      return null;
    }
    if (data) return mapRow(data);
  } catch (err) {
    console.warn('[INVOICES] refetchFromView exception:', err);
  }
  return null;
};

const refetchManyFromView = async (idValues) => {
  try {
    const { data, error } = await supabase
      .from(INVOICES_READ_SOURCE)
      .select('*')
      .in('id', idValues)
      .is('deleted_at', null);
    if (error) {
      console.warn('[INVOICES] refetchManyFromView error:', error.message);
      return null;
    }
    if (data) return new Map(data.map(r => [normalizeId(r.id), mapRow(r)]));
  } catch (err) {
    console.warn('[INVOICES] refetchManyFromView exception:', err);
  }
  return null;
};

// ============================================================================
// HOOK
// ============================================================================
export const useSupabaseInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [allInvoices, setAllInvoices] = useState([]); // all invoices across all projects (for payment inbox, radar)
  const [loading, setLoading] = useState(false);

  // Feature 4: Bank payment events state
  const [paymentEvents, setPaymentEvents] = useState([]);
  const [paymentEventsLoading, setPaymentEventsLoading] = useState(false);
  // Global count of payment events awaiting action (suggested + unmatched/new),
  // independent of any project filter — powers the nav/radar "payment received" badge.
  const [pendingPaymentCount, setPendingPaymentCount] = useState(0);

  // Archived (confirmed) payment events — paginated
  const [archivedPaymentEvents, setArchivedPaymentEvents] = useState([]);
  const [archivedPaymentEventsLoading, setArchivedPaymentEventsLoading] = useState(false);
  const [archivedPaymentPage, setArchivedPaymentPage] = useState(0);
  const [archivedPaymentHasNext, setArchivedPaymentHasNext] = useState(false);

  // Expense payments (ad-hoc, non-invoice) — for Project Expenses page
  const [expensePayments, setExpensePayments] = useState([]);
  const [expensePaymentsLoading, setExpensePaymentsLoading] = useState(false);

  // ------------------------------------------------------------------
  // FETCH — by project (admin + supervisor only; RLS enforced server-side)
  // ------------------------------------------------------------------
  const fetchInvoices = useCallback(async (projectId) => {
    if (!projectId) return;
    setLoading(true);
    try {
      // Feature 4: Join suggested bank_payment_events via suggested_invoice_id FK
      const { data, error } = await supabase
        .from(INVOICES_READ_SOURCE)
        .select('*, bank_payment_events!bank_payment_events_suggested_invoice_id_fkey(id, amount, txn_date, payer_name, narration, suggested_invoice_id, suggested_confidence, suggested_reasons, status)')
        .eq('project_id', projectId)
        .is('deleted_at', null)
        .order('invoice_date', { ascending: false });

      if (error) throw error;
      setInvoices((data || []).map(r => enrichWithPaymentEvent(mapRow(r), r)));
    } catch (err) {
      console.error('[INVOICES] fetch error:', err);
      // Fallback: if the join fails (FK not yet created), fetch without join
      try {
        const { data: fallbackData, error: fallbackErr } = await supabase
          .from(INVOICES_READ_SOURCE)
          .select('*')
          .eq('project_id', projectId)
          .is('deleted_at', null)
          .order('invoice_date', { ascending: false });
        if (fallbackErr) throw fallbackErr;
        setInvoices((fallbackData || []).map(mapRow));
      } catch (fallbackErr2) {
        console.error('[INVOICES] fallback fetch error:', fallbackErr2);
        setInvoices([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Keep the cross-project list in step with a create or update made here.
  const upsertIntoAllInvoices = useCallback((row) => {
    if (!row?.id) return;
    setAllInvoices(prev => (prev.some(i => i.id === row.id)
      ? prev.map(i => (i.id === row.id ? row : i))
      : [row, ...prev]));
  }, []);

  // ------------------------------------------------------------------
  // FETCH ALL — all invoices across all projects (for payment inbox, radar)
  // Writes to separate allInvoices state so it doesn't overwrite project-scoped invoices.
  // ------------------------------------------------------------------
  const fetchAllInvoices = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from(INVOICES_READ_SOURCE)
        .select('*')
        .is('deleted_at', null)
        .order('invoice_date', { ascending: false });
      if (error) throw error;
      setAllInvoices((data || []).map(mapRow));
    } catch (err) {
      console.error('[INVOICES] fetchAll error:', err);
      setAllInvoices([]);
    }
  }, []);

  // ------------------------------------------------------------------
  // FETCH BY STAGE — get invoices for a specific project + stage_key
  // Used by TimelinePage to check paid_confirmed status
  // ------------------------------------------------------------------
  const fetchInvoicesByStage = useCallback(async (projectId, stageKey) => {
    if (!projectId || !stageKey) return [];
    try {
      const { data, error } = await supabase
        .from(INVOICES_READ_SOURCE)
        .select('*')
        .eq('project_id', projectId)
        .eq('stage_key', stageKey)
        .is('deleted_at', null)
        .order('invoice_date', { ascending: false });

      if (error) throw error;
      return (data || []).map(mapRow);
    } catch (err) {
      console.error('[INVOICES] fetchByStage error:', err);
      return [];
    }
  }, []);

  // ------------------------------------------------------------------
  // FETCH INVOICE SUMMARY FOR PROJECT — lightweight summary for Timeline
  // Returns: { [stageKey]: {
  //   // Tax invoices (invoice_doc_type !== 'proforma')
  //   hasIssuedInvoice, hasDraftInvoice, hasPaidConfirmed, paidDate,
  //   lastInvoiceDate, lastIssuedInvoiceDate,
  //   // Proforma invoices (invoice_doc_type === 'proforma') — tracked separately
  //   // so proforma dates surface in the Timeline "Proforma" column instead of
  //   // leaking into the tax "Invoice" column.
  //   hasProformaInvoice, hasDraftProforma, lastProformaInvoiceDate, lastIssuedProformaDate,
  // } }
  // ------------------------------------------------------------------
  const fetchInvoiceSummaryForProject = useCallback(async (projectId) => {
    if (!projectId) return {};
    try {
      const { data, error } = await supabase
        .from(INVOICES_READ_SOURCE)
        .select('stage_key, status, paid_confirmed, paid_date, invoice_date, invoice_doc_type')
        .eq('project_id', projectId)
        .is('deleted_at', null);

      if (error) throw error;

      const summary = {};
      (data || []).forEach(row => {
        const key = row.stage_key;
        if (!key) return;
        if (!summary[key]) {
          summary[key] = {
            hasIssuedInvoice: false,
            hasDraftInvoice: false,
            hasPaidConfirmed: false,
            paidDate: null,
            lastInvoiceDate: null,
            lastIssuedInvoiceDate: null,
            // Proforma-specific (never mixed into the tax aggregates above)
            hasProformaInvoice: false,
            hasDraftProforma: false,
            lastProformaInvoiceDate: null,
            lastIssuedProformaDate: null,
          };
        }
        const s = summary[key];
        const invDate = row.invoice_date || null;

        // Proforma invoices are a distinct document type; keep them out of the
        // tax-invoice aggregates so the two columns stay independent.
        if (row.invoice_doc_type === 'proforma') {
          s.hasProformaInvoice = true;
          if (row.status === 'draft') s.hasDraftProforma = true;
          if (row.status === 'issued' || row.status === 'paid') {
            if (invDate && (!s.lastIssuedProformaDate || invDate > s.lastIssuedProformaDate)) {
              s.lastIssuedProformaDate = invDate;
            }
          }
          if (invDate && (!s.lastProformaInvoiceDate || invDate > s.lastProformaInvoiceDate)) {
            s.lastProformaInvoiceDate = invDate;
          }
          return;
        }

        if (row.status === 'issued' || row.status === 'paid') {
          s.hasIssuedInvoice = true;
          if (invDate && (!s.lastIssuedInvoiceDate || invDate > s.lastIssuedInvoiceDate)) {
            s.lastIssuedInvoiceDate = invDate;
          }
        }
        if (row.status === 'draft') {
          s.hasDraftInvoice = true;
        }
        // Track most recent invoice date overall (any status)
        if (invDate && (!s.lastInvoiceDate || invDate > s.lastInvoiceDate)) {
          s.lastInvoiceDate = invDate;
        }
        if (row.paid_confirmed) {
          s.hasPaidConfirmed = true;
          if (!s.paidDate || (row.paid_date && row.paid_date > s.paidDate)) {
            s.paidDate = row.paid_date;
          }
        }
      });
      return summary;
    } catch (err) {
      console.error('[INVOICES] fetchInvoiceSummary error:', err);
      return {};
    }
  }, []);

  // ------------------------------------------------------------------
  // ALLOCATE INVOICE NUMBER — Feature 1
  // ------------------------------------------------------------------
  const allocateInvoiceNumber = async (invoiceDate) => {
    try {
      await requireSession();
      if (!invoiceDate) {
        return { success: false, error: 'Invoice date is required to allocate a number.' };
      }
      const { data, error } = await supabase
        .rpc('allocate_invoice_number', { p_invoice_date: invoiceDate });
      if (error) throw error;
      return { success: true, invoiceNumber: data };
    } catch (err) {
      console.error('[INVOICES] allocateInvoiceNumber error:', err);
      return {
        success: false,
        error: formatSupabaseError(err, { action: 'allocate invoice number', entity: 'invoice' }),
      };
    }
  };

  // ------------------------------------------------------------------
  // CREATE — admin + supervisor; status always starts as 'draft'
  // ------------------------------------------------------------------
  const createInvoice = async (invoiceData) => {
    try {
      const authUserId = await requireSession();

      const base   = Number(invoiceData.baseAmount)  || 0;
      const gstPct = Number(invoiceData.gstPercent)  || 0;
      const tdsPct = Number(invoiceData.tdsPercent)   || 0;
      const shouldApplyTds = invoiceData.applyTds !== false; // default true

      const gross  = base + (base * gstPct / 100);
      const tds    = shouldApplyTds ? (base * tdsPct / 100) : 0;
      const net    = gross - tds;

      const dbData = {
        project_id:      invoiceData.projectId,
        stage_key:       invoiceData.stageKey       || null,
        invoice_number:  invoiceData.invoiceNumber   || '',
        invoice_date:    invoiceData.invoiceDate     || null,
        base_amount:     base,
        gst_percent:     gstPct,
        tds_percent:     tdsPct,
        gross_amount:    gross,
        tds_amount:      tds,
        net_amount:      net,
        status:          'draft',
        paid_confirmed:  false,
        paid_date:       null,
        note:            invoiceData.note || '',
        is_manual_override: invoiceData.isManualOverride || false,
        basis_mode:      invoiceData.basisMode || 'percent',
        created_by:      authUserId,
      };

      // Optional: account_name on create if admin overrides
      if (invoiceData.accountName) {
        dbData.account_name = invoiceData.accountName;
      }

      // Office location (defaults handled by caller)
      if (invoiceData.officeLocation) {
        dbData.office_location = invoiceData.officeLocation;
      }

      // Explicit invoice currency
      if (invoiceData.currency) {
        dbData.currency = invoiceData.currency;
      }

      // Tax regime (Feature B) + TDS optional (Feature C)
      if (invoiceData.taxRegime !== undefined) {
        dbData.tax_regime = invoiceData.taxRegime || null;
      }
      if (invoiceData.customTaxPercent !== undefined) {
        dbData.custom_tax_percent = invoiceData.customTaxPercent;
      }
      if (invoiceData.applyTds !== undefined) {
        dbData.apply_tds = invoiceData.applyTds;
      }
      if (invoiceData.invoiceDocType) {
        dbData.invoice_doc_type = invoiceData.invoiceDocType;
      }
      if (invoiceData.customStageName) {
        dbData.custom_stage_name = invoiceData.customStageName;
      }
      if (invoiceData.sacCode !== undefined) {
        dbData.sac_code = invoiceData.sacCode || null;
      }

      // Optional: fee revision snapshot fields (pre-baked during draft creation)
      if (invoiceData.feeRevisionId) {
        dbData.fee_revision_id = invoiceData.feeRevisionId;
      }
      if (invoiceData.totalFeeSnapshot != null) {
        dbData.total_fee_snapshot = invoiceData.totalFeeSnapshot;
      }
      if (invoiceData.invoicePercentSnapshot != null) {
        dbData.invoice_percent_snapshot = invoiceData.invoicePercentSnapshot;
      }
      if (invoiceData.baseAmountPlanned != null) {
        dbData.base_amount_planned = invoiceData.baseAmountPlanned;
      }
      if (invoiceData.adjustmentAmount != null) {
        dbData.adjustment_amount = invoiceData.adjustmentAmount;
      }
      if (invoiceData.recalcBreakdownJson) {
        dbData.recalc_breakdown_json = invoiceData.recalcBreakdownJson;
      }
      if (invoiceData.recalcExplanationText) {
        dbData.recalc_explanation_text = invoiceData.recalcExplanationText;
      }

      // ------------------------------------------------------------------
      // C2) Defensive insert: retry without office_location if column missing
      // Migration: ALTER TABLE public.invoices ADD COLUMN IF NOT EXISTS office_location text;
      // ------------------------------------------------------------------
      let data, error;
      let officeLocationWarning = null;

      const insertResult = await supabase
        .from(INVOICES_WRITE_SOURCE)
        .insert([dbData])
        .select()
        .single();

      data = insertResult.data;
      error = insertResult.error;

      if (error && (
        error.message?.includes('office_location') ||
        error.message?.includes('schema cache') ||
        error.message?.includes('Could not find')
      )) {
        // Retry without office_location
        console.warn('[INVOICES] office_location column not found — retrying without it');
        const retryData = { ...dbData };
        delete retryData.office_location;
        const retryResult = await supabase
          .from(INVOICES_WRITE_SOURCE)
          .insert([retryData])
          .select()
          .single();
        data = retryResult.data;
        error = retryResult.error;
        if (!error) {
          officeLocationWarning = 'Office location not saved (DB column missing). Run: ALTER TABLE public.invoices ADD COLUMN IF NOT EXISTS office_location text;';
        }
      }

      if (error) {
        if (error.code === '23505' && error.message?.includes('invoice_number')) {
          throw new Error(
            `Invoice number "${invoiceData.invoiceNumber}" is already in use. ` +
            'Please use a different number or let the system auto-assign one.'
          );
        }
        throw error;
      }

      // Re-fetch from reporting view for correct INR amounts
      const viewRow = await refetchFromView(normalizeId(data.id));
      const mapped = viewRow || mapRow(data);
      setInvoices(prev => [mapped, ...prev]);
      // The next-number suggestion reads allInvoices, which is otherwise only
      // loaded on page open: without this, every invoice after the first in a
      // session is offered the same number until a refresh.
      upsertIntoAllInvoices(mapped);
      return { success: true, data: mapped, warning: officeLocationWarning };
    } catch (err) {
      console.error('[INVOICES] create error:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'invoice' }) };
    }
  };

  // ------------------------------------------------------------------
  // UPDATE — admin + supervisor; ONLY while status = 'draft'
  // ------------------------------------------------------------------
  const updateInvoice = async (invoiceId, updates) => {
    try {
      await requireSession();
      if (!invoiceId) throw new Error('updateInvoice: missing invoiceId');

      const existing = invoices.find(i => i.id === normalizeId(invoiceId));
      if (existing && existing.status !== 'draft') {
        return { success: false, error: 'Cannot edit an invoice that has been issued.' };
      }

      const dbUpdates = {};
      if (updates.invoiceNumber !== undefined)  dbUpdates.invoice_number = updates.invoiceNumber;
      if (updates.invoiceDate !== undefined)    dbUpdates.invoice_date   = updates.invoiceDate || null;
      if (updates.stageKey !== undefined)       dbUpdates.stage_key      = updates.stageKey || null;
      if (updates.note !== undefined)           dbUpdates.note           = updates.note;
      if (updates.isManualOverride !== undefined) dbUpdates.is_manual_override = updates.isManualOverride;
      if (updates.accountName !== undefined)    dbUpdates.account_name   = updates.accountName || null;
      if (updates.officeLocation !== undefined) dbUpdates.office_location = updates.officeLocation || null;
      // Editing an invoice's office has to move its currency with it; without
      // this the column keeps whatever it was created with.
      if (updates.currency !== undefined)       dbUpdates.currency        = updates.currency;
      if (updates.basisMode !== undefined)      dbUpdates.basis_mode     = updates.basisMode;
      // Tax regime (Feature B) + TDS optional (Feature C)
      if (updates.taxRegime !== undefined)       dbUpdates.tax_regime         = updates.taxRegime || null;
      if (updates.customTaxPercent !== undefined) dbUpdates.custom_tax_percent = updates.customTaxPercent;
      if (updates.applyTds !== undefined)        dbUpdates.apply_tds          = updates.applyTds;
      if (updates.invoiceDocType !== undefined)  dbUpdates.invoice_doc_type   = updates.invoiceDocType || 'tax';
      if (updates.customStageName !== undefined) dbUpdates.custom_stage_name  = updates.customStageName || null;
      if (updates.sacCode !== undefined)         dbUpdates.sac_code           = updates.sacCode || null;

      // Recalculate amounts if any amount field changed
      if (updates.baseAmount !== undefined || updates.gstPercent !== undefined || updates.tdsPercent !== undefined || updates.applyTds !== undefined) {
        const base   = Number(updates.baseAmount  ?? existing?.baseAmount  ?? 0);
        const gstPct = Number(updates.gstPercent  ?? existing?.gstPercent  ?? 0);
        const tdsPct = Number(updates.tdsPercent  ?? existing?.tdsPercent  ?? 0);
        const shouldApplyTds = updates.applyTds !== undefined ? updates.applyTds : (existing?.applyTds !== false);

        dbUpdates.base_amount  = base;
        dbUpdates.gst_percent  = gstPct;
        dbUpdates.tds_percent  = tdsPct;
        dbUpdates.gross_amount = base + (base * gstPct / 100);
        dbUpdates.tds_amount   = shouldApplyTds ? (base * tdsPct / 100) : 0;
        dbUpdates.net_amount   = dbUpdates.gross_amount - dbUpdates.tds_amount;
      }

      if (Object.keys(dbUpdates).length === 0) {
        return { success: false, error: 'No updates provided.' };
      }

      const idValue = normalizeId(invoiceId);

      // C2) Defensive update: retry without office_location if column missing
      let data, error;
      let officeLocationWarning = null;

      const updateResult = await supabase
        .from(INVOICES_WRITE_SOURCE)
        .update(dbUpdates)
        .eq('id', idValue)
        .is('deleted_at', null)
        .select()
        .maybeSingle();

      data = updateResult.data;
      error = updateResult.error;

      if (error && (
        error.message?.includes('office_location') ||
        error.message?.includes('schema cache') ||
        error.message?.includes('Could not find')
      )) {
        console.warn('[INVOICES] office_location column not found on update — retrying without it');
        const retryUpdates = { ...dbUpdates };
        delete retryUpdates.office_location;
        const retryResult = await supabase
          .from(INVOICES_WRITE_SOURCE)
          .update(retryUpdates)
          .eq('id', idValue)
          .is('deleted_at', null)
          .select()
          .maybeSingle();
        data = retryResult.data;
        error = retryResult.error;
        if (!error) {
          officeLocationWarning = 'Office location not saved (DB column missing).';
        }
      }

      if (error) {
        if (error.code === '23505' && error.message?.includes('invoice_number')) {
          throw new Error(
            `Invoice number "${updates.invoiceNumber}" is already in use. ` +
            'Please use a different number or let the system auto-assign one.'
          );
        }
        throw error;
      }
      if (!data) throw new Error('Invoice not found or already deleted.');

      // Re-fetch from reporting view for correct INR amounts
      const viewRow = await refetchFromView(idValue);
      const mapped = viewRow || mapRow(data);
      setInvoices(prev => prev.map(i => i.id === idValue ? mapped : i));
      upsertIntoAllInvoices(mapped);
      return { success: true, data: mapped, warning: officeLocationWarning };
    } catch (err) {
      console.error('[INVOICES] update error:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'invoice' }) };
    }
  };

  // ------------------------------------------------------------------
  // ISSUE — locks the invoice (draft → issued)
  // Now also stores frozen snapshots and transparency artifacts.
  // ------------------------------------------------------------------
  // snapshotData (optional): {
  //   totalFeeSnapshot, invoicePercentSnapshot, baseAmountPlanned,
  //   adjustmentAmount, basisMode, feeRevisionId,
  //   recalcBreakdownJson, recalcExplanationText
  // }
  // ------------------------------------------------------------------
  const issueInvoice = async (invoiceId, snapshotData = {}) => {
    try {
      await requireSession();
      if (!invoiceId) throw new Error('issueInvoice: missing invoiceId');

      const existing = invoices.find(i => i.id === normalizeId(invoiceId));
      if (existing && existing.status !== 'draft') {
        return { success: false, error: 'Only draft invoices can be issued.' };
      }

      const idValue = normalizeId(invoiceId);

      // Build snapshot portion of payload (invariant across retries)
      const snapshotPayload = {};
      if (snapshotData.totalFeeSnapshot != null) {
        snapshotPayload.total_fee_snapshot = snapshotData.totalFeeSnapshot;
      }
      if (snapshotData.invoicePercentSnapshot != null) {
        snapshotPayload.invoice_percent_snapshot = snapshotData.invoicePercentSnapshot;
      }
      if (snapshotData.baseAmountPlanned != null) {
        snapshotPayload.base_amount_planned = snapshotData.baseAmountPlanned;
      }
      if (snapshotData.adjustmentAmount != null) {
        snapshotPayload.adjustment_amount = snapshotData.adjustmentAmount;
      }
      if (snapshotData.basisMode) {
        snapshotPayload.basis_mode = snapshotData.basisMode;
      }
      if (snapshotData.feeRevisionId) {
        snapshotPayload.fee_revision_id = snapshotData.feeRevisionId;
      }
      if (snapshotData.recalcBreakdownJson) {
        snapshotPayload.recalc_breakdown_json = snapshotData.recalcBreakdownJson;
      }
      if (snapshotData.recalcExplanationText) {
        snapshotPayload.recalc_explanation_text = snapshotData.recalcExplanationText;
      }

      // Retry loop: handle 23505 unique violation on invoice_number
      const MAX_TRIES = 3;
      let lastErr = null;
      // If caller pre-resolved a number (e.g. reassigned from an on-hold PI), use it directly
      let allocatedNumber = snapshotData.resolvedInvoiceNumber || null;
      if (allocatedNumber) {
        snapshotPayload.is_manual_override = true;
      }

      // On first attempt, try the stored number (if any); on collision, fall back to RPC.
      const currentNumber = existing?.invoiceNumber?.trim();
      let usedStoredNumber = false;

      for (let attempt = 1; attempt <= MAX_TRIES; attempt++) {
        const updatePayload = { status: 'issued', ...snapshotPayload };

        let numberAllocated = false;
        if (!allocatedNumber) {
          if (currentNumber && !usedStoredNumber) {
            // First try: honour whatever number is stored on the draft
            allocatedNumber = currentNumber;
            usedStoredNumber = true;
          } else {
            // No stored number, or the stored number just collided — allocate via RPC
            const invDate = existing?.invoiceDate;
            if (!invDate) {
              return { success: false, error: 'Set invoice date before issuing.' };
            }
            const allocResult = await allocateInvoiceNumber(invDate);
            if (!allocResult.success || !allocResult.invoiceNumber) {
              return { success: false, error: allocResult.error || 'Failed to allocate invoice number.' };
            }
            allocatedNumber = allocResult.invoiceNumber;
            numberAllocated = true;
            console.log('[INVOICES] number allocated on issue:', allocatedNumber, 'for id:', idValue, '(attempt', attempt + ')');
          }
        }
        if (allocatedNumber) {
          updatePayload.invoice_number = allocatedNumber;
          numberAllocated = true;
        }

        const { data, error } = await supabase
          .from(INVOICES_WRITE_SOURCE)
          .update(updatePayload)
          .eq('id', idValue)
          .is('deleted_at', null)
          .select()
          .maybeSingle();

        if (!error && data) {
          // Re-fetch from reporting view for correct INR amounts
          const viewRow = await refetchFromView(idValue);
          const mapped = viewRow || mapRow(data);
          setInvoices(prev => prev.map(i => i.id === idValue ? mapped : i));
          return { success: true, data: mapped, numberAllocated };
        }

        // Check for unique violation (23505)
        const isUniqueViolation = error &&
          (error.code === '23505' || (typeof error.message === 'string' && error.message.includes('duplicate key')));

        if (!isUniqueViolation) {
          // Not a collision — throw immediately
          throw error || new Error('Invoice not found.');
        }

        // Collision: clear allocated number so next iteration re-allocates
        console.warn(`[INVOICES] 23505 collision on issue attempt ${attempt}, invoice_number="${allocatedNumber}". Retrying...`);
        lastErr = error;
        allocatedNumber = null;
      }

      // All retries exhausted
      throw lastErr || new Error('Could not issue invoice: duplicate invoice number after retries.');
    } catch (err) {
      console.error('[INVOICES] issue error:', err);
      const is23505 = err?.code === '23505' || (typeof err?.message === 'string' && err.message.includes('duplicate key'));
      return {
        success: false,
        error: is23505
          ? 'Duplicate invoice number collision. Please retry.'
          : formatSupabaseError(err, { action: 'issue', entity: 'invoice' }),
      };
    }
  };

  // ------------------------------------------------------------------
  // CONFIRM PAYMENT — admin + supervisor; issued → paid
  // Feature 2 — Account Name Learning included
  // ------------------------------------------------------------------
  const confirmPayment = async (invoiceId, paidDate, paymentEvent = {}) => {
    try {
      await requireSession();
      if (!invoiceId) throw new Error('confirmPayment: missing invoiceId');

      const existing = invoices.find(i => i.id === normalizeId(invoiceId));
      if (existing && existing.status === 'draft') {
        return { success: false, error: 'Issue the invoice before confirming payment.' };
      }
      if (existing && existing.status === 'paid') {
        return { success: false, error: 'Payment already confirmed.' };
      }

      const idValue = normalizeId(invoiceId);
      const dateValue = paidDate || new Date().toISOString().slice(0, 10);

      const resolvedAccountName =
        paymentEvent.accountName ||
        paymentEvent.payerName ||
        paymentEvent.account_name ||
        paymentEvent.payer_name ||
        null;

      const updatePayload = {
        status: 'paid',
        paid_confirmed: true,
        paid_date: dateValue,
      };

      if (resolvedAccountName) {
        updatePayload.account_name = resolvedAccountName;
      }

      const { data, error } = await supabase
        .from(INVOICES_WRITE_SOURCE)
        .update(updatePayload)
        .eq('id', idValue)
        .is('deleted_at', null)
        .select()
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error('Invoice not found.');

      // Feature 2: Upsert into project_account_names
      if (resolvedAccountName && existing?.projectId) {
        try {
          await supabase
            .from('project_account_names')
            .upsert(
              {
                project_id: existing.projectId,
                account_name: resolvedAccountName,
                first_seen_invoice_id: idValue,
              },
              { onConflict: 'project_id,account_name', ignoreDuplicates: true }
            );
        } catch (learnErr) {
          console.warn('[INVOICES] account name learning failed (non-blocking):', learnErr);
        }
      }

      // Re-fetch from reporting view for correct INR amounts
      const viewRow = await refetchFromView(idValue);
      const mapped = viewRow || mapRow(data);
      setInvoices(prev => prev.map(i => i.id === idValue ? mapped : i));
      return { success: true, data: mapped };
    } catch (err) {
      console.error('[INVOICES] confirmPayment error:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'confirm payment', entity: 'invoice' }) };
    }
  };

  // ------------------------------------------------------------------
  // UPSERT PROJECT ACCOUNT NAME — standalone helper
  // Can be called independently of confirmPayment when needed.
  // ------------------------------------------------------------------
  const upsertProjectAccountName = async (projectId, accountName, firstSeenInvoiceId) => {
    try {
      if (!projectId || !accountName) return { success: false, error: 'projectId and accountName required' };
      await requireSession();
      await supabase
        .from('project_account_names')
        .upsert(
          {
            project_id: projectId,
            account_name: accountName,
            first_seen_invoice_id: firstSeenInvoiceId || null,
          },
          { onConflict: 'project_id,account_name', ignoreDuplicates: true }
        );
      return { success: true };
    } catch (err) {
      console.warn('[INVOICES] upsertProjectAccountName error (non-blocking):', err);
      return { success: false, error: err.message };
    }
  };

  // ------------------------------------------------------------------
  // FETCH LEARNED ACCOUNT NAMES — for a project
  // ------------------------------------------------------------------
  const fetchProjectAccountNames = async (projectId) => {
    try {
      if (!projectId) return { success: true, data: [] };

      const { data, error } = await supabase
        .from('project_account_names')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return {
        success: true,
        data: (data || []).map(r => ({
          id:                 r.id,
          projectId:          normalizeId(r.project_id),
          accountName:        r.account_name,
          firstSeenInvoiceId: r.first_seen_invoice_id ? normalizeId(r.first_seen_invoice_id) : null,
          createdAt:          r.created_at,
        })),
      };
    } catch (err) {
      console.error('[INVOICES] fetchProjectAccountNames error:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'fetch', entity: 'project account names' }) };
    }
  };

  // ------------------------------------------------------------------
  // HELPERS — for recalculation preview (pure functions, no DB calls)
  // ------------------------------------------------------------------

  /**
   * Get all issued/paid invoices for a stage (from local state)
   */
  const getIssuedInvoicesForStage = useCallback((stageKey) => {
    return invoices.filter(
      inv => inv.stageKey === stageKey && (inv.status === 'issued' || inv.status === 'paid')
    );
  }, [invoices]);

  /**
   * Get ALL issued/paid invoices for the current project (no stage filter).
   * Used by RecalculationPreviewModal for invoice-level revaluation (7-step logic).
   */
  const getIssuedInvoices = useCallback(() => {
    return invoices.filter(
      inv => (inv.status === 'issued' || inv.status === 'paid') && !inv.deletedAt
    );
  }, [invoices]);

  /**
   * Get total already-invoiced base amount for a stage (frozen facts)
   */
  const getInvoicedBaseForStage = useCallback((stageKey) => {
    return getIssuedInvoicesForStage(stageKey)
      .reduce((sum, inv) => sum + (Number(inv.baseAmount) || 0), 0);
  }, [getIssuedInvoicesForStage]);

  /**
   * Get the date of the last issued invoice across all stages
   */
  const getLastIssuedDate = useCallback(() => {
    const issued = invoices.filter(inv => inv.status === 'issued' || inv.status === 'paid');
    if (issued.length === 0) return null;
    return issued.reduce((latest, inv) => {
      if (!latest || (inv.invoiceDate && inv.invoiceDate > latest)) return inv.invoiceDate;
      return latest;
    }, null);
  }, [invoices]);

  // ------------------------------------------------------------------
  // CONFIRM PAYMENT MATCH — Feature 4
  // Step 5: Idempotency — blocks if already paid or event already confirmed
  // ------------------------------------------------------------------
  const confirmPaymentMatch = async ({ invoiceId, paymentEvent: pe, invoice: passedInvoice }) => {
    try {
      await requireSession();
      if (!invoiceId || !pe?.id) throw new Error('confirmPaymentMatch: missing invoiceId or paymentEvent');

      const idValue = normalizeId(invoiceId);
      const existing = passedInvoice || invoices.find(i => i.id === idValue);

      if (existing && existing.status === 'draft') {
        return { success: false, error: 'Issue the invoice before confirming payment.' };
      }
      // Step 5: idempotency
      if (existing && (existing.paidConfirmed || existing.status === 'paid')) {
        return { success: false, error: 'This invoice is already confirmed as paid.' };
      }
      if (pe.status === 'confirmed') {
        return { success: false, error: 'This payment event has already been confirmed.' };
      }

      const invoicePayload = {
        status: 'paid',
        paid_confirmed: true,
        paid_date: pe.txnDate || pe.txn_date || new Date().toISOString().slice(0, 10),
        payment_event_id: pe.id,
      };
      const payerName = pe.payerName || pe.payer_name || '';
      if (payerName && !existing?.accountName) {
        invoicePayload.account_name = payerName;
      }

      const { data: invData, error: invErr } = await supabase
        .from(INVOICES_WRITE_SOURCE)
        .update(invoicePayload)
        .eq('id', idValue)
        .is('deleted_at', null)
        .select()
        .maybeSingle();

      if (invErr) throw invErr;
      if (!invData) throw new Error('Invoice not found.');

      try {
        await supabase
          .from('bank_payment_events')
          .update({ status: 'confirmed', confirmed_invoice_id: idValue, confirmed_at: new Date().toISOString() })
          .eq('id', pe.id);
      } catch (bpeErr) {
        console.warn('[INVOICES] bank_payment_events update failed (non-blocking):', bpeErr);
      }

      if (payerName && existing?.projectId) {
        try {
          await supabase
            .from('project_account_names')
            .upsert(
              { project_id: existing.projectId, account_name: payerName, first_seen_invoice_id: idValue },
              { onConflict: 'project_id,account_name', ignoreDuplicates: true }
            );
        } catch (learnErr) {
          console.warn('[INVOICES] account name learning failed (non-blocking):', learnErr);
        }
      }

      // Re-fetch from reporting view for correct INR amounts
      const viewRow = await refetchFromView(idValue);
      const mapped = viewRow || mapRow(invData);
      mapped.paymentDetected = false;
      mapped.paymentEvent = null;
      setInvoices(prev => prev.map(i => i.id === idValue ? mapped : i));
      return { success: true, data: mapped };
    } catch (err) {
      console.error('[INVOICES] confirmPaymentMatch error:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'confirm payment match', entity: 'invoice' }) };
    }
  };

  // ------------------------------------------------------------------
  // FETCH PAYMENT EVENTS — Step 1: Inbox data
  // ------------------------------------------------------------------
  const mapPaymentEvent = (r) => ({
    id:                  r.id,
    amount:              Number(r.amount) || 0,
    txnDate:             r.txn_date ?? null,
    payerName:           r.payer_name ?? '',
    narration:           r.narration ?? '',
    suggestedInvoiceId:  r.suggested_invoice_id ? normalizeId(r.suggested_invoice_id) : null,
    suggestedConfidence: Number(r.suggested_confidence) || 0,
    suggestedReasons:    r.suggested_reasons ?? null,
    status:              r.status ?? 'new',
    confirmedInvoiceId:  r.confirmed_invoice_id ? normalizeId(r.confirmed_invoice_id) : null,
    confirmedAt:         r.confirmed_at ?? null,
    createdAt:           r.created_at ?? null,
    suggestedInvoiceNumber: r.invoices?.invoice_number ?? null,
    suggestedProjectId:  r.invoices?.project_id ? normalizeId(r.invoices.project_id) : null,
    // Email metadata for display: subject, body snippet, email sender
    emailSubject:        r.raw?.subject ?? null,
    emailBody:           r.raw?.text ?? null,
    emailFrom:           r.raw?.from ?? null,
    emailDate:           r.raw?.date ?? r.created_at ?? null,
    // Expense-payment flag
    expensePayment:      r.expense_payment ?? false,
  });

  const fetchPaymentEvents = useCallback(async (projectId) => {
    setPaymentEventsLoading(true);
    try {
      const { data, error } = await supabase
        .from('bank_payment_events')
        .select('*, invoices!bank_payment_events_suggested_invoice_id_fkey(invoice_number, project_id)')
        .in('status', ['suggested', 'unmatched', 'new'])
        .order('txn_date', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;

      let events = data || [];
      // When a projectId is given, filter to that project + unmatched (legacy behavior)
      if (projectId) {
        events = events.filter(r => {
          if (r.invoices?.project_id && normalizeId(r.invoices.project_id) === normalizeId(projectId)) return true;
          if (!r.suggested_invoice_id && (r.status === 'unmatched' || r.status === 'new')) return true;
          return false;
        });
      }

      setPaymentEvents(events.map(mapPaymentEvent));
    } catch (err) {
      console.error('[INVOICES] fetchPaymentEvents error:', err);
      setPaymentEvents([]);
    } finally {
      setPaymentEventsLoading(false);
    }
  }, []);

  // Count all pending payment events (suggested + unmatched/new) regardless of
  // project scope. Kept separate from `paymentEvents` because that list gets
  // narrowed to a single project when the inbox is opened for one — the badge
  // count must stay global. Gated by RLS on bank_payment_events.
  const fetchPendingPaymentCount = useCallback(async () => {
    try {
      const { count, error } = await supabase
        .from('bank_payment_events')
        .select('id', { count: 'exact', head: true })
        .in('status', ['suggested', 'unmatched', 'new']);
      if (error) throw error;
      setPendingPaymentCount(count || 0);
    } catch (err) {
      console.error('[INVOICES] fetchPendingPaymentCount error:', err);
    }
  }, []);

  const refreshPaymentEvents = useCallback(async (projectId) => {
    await fetchPaymentEvents(projectId);
    // Keep the global badge count fresh after any inbox action, even when the
    // refresh above was narrowed to a single project.
    await fetchPendingPaymentCount();
  }, [fetchPaymentEvents, fetchPendingPaymentCount]);

  // ------------------------------------------------------------------
  // FETCH ARCHIVED (CONFIRMED) PAYMENT EVENTS — paginated, 20 per page
  // ------------------------------------------------------------------
  const ARCHIVED_PAGE_SIZE = 20;

  const fetchArchivedPaymentEvents = useCallback(async (projectId, page = 0) => {
    if (!projectId) { setArchivedPaymentEvents([]); return; }
    setArchivedPaymentEventsLoading(true);
    try {
      const from = page * ARCHIVED_PAGE_SIZE;
      const to = from + ARCHIVED_PAGE_SIZE; // fetch one extra to detect next page

      const { data, error } = await supabase
        .from('bank_payment_events')
        .select('*, invoices!bank_payment_events_confirmed_invoice_id_fkey(invoice_number, project_id)')
        .eq('status', 'confirmed')
        .not('confirmed_at', 'is', null)
        .order('confirmed_at', { ascending: false })
        .range(from, to);

      if (error) throw error;

      // Filter to project scope (confirmed events have confirmed_invoice_id → invoices.project_id)
      const projectEvents = (data || []).filter(r => {
        if (r.invoices?.project_id && normalizeId(r.invoices.project_id) === normalizeId(projectId)) return true;
        return false;
      });

      // Detect if there are more pages: if we got more than PAGE_SIZE results
      const hasNext = projectEvents.length > ARCHIVED_PAGE_SIZE;
      const pageItems = hasNext ? projectEvents.slice(0, ARCHIVED_PAGE_SIZE) : projectEvents;

      setArchivedPaymentEvents(pageItems.map(r => ({
        id:                  r.id,
        amount:              Number(r.amount) || 0,
        txnDate:             r.txn_date ?? null,
        payerName:           r.payer_name ?? '',
        narration:           r.narration ?? '',
        confirmedInvoiceId:  r.confirmed_invoice_id ? normalizeId(r.confirmed_invoice_id) : null,
        confirmedAt:         r.confirmed_at ?? null,
        status:              r.status,
        invoiceNumber:       r.invoices?.invoice_number ?? null,
      })));
      setArchivedPaymentPage(page);
      setArchivedPaymentHasNext(hasNext);
    } catch (err) {
      console.error('[INVOICES] fetchArchivedPaymentEvents error:', err);
      setArchivedPaymentEvents([]);
      setArchivedPaymentHasNext(false);
    } finally {
      setArchivedPaymentEventsLoading(false);
    }
  }, []);

  // Derived lists
  const suggestedPaymentEvents = paymentEvents.filter(e => e.status === 'suggested');
  const unmatchedPaymentEvents = paymentEvents.filter(e => e.status === 'unmatched' || e.status === 'new');

  // ------------------------------------------------------------------
  // IGNORE PAYMENT EVENT — Step 1 + Step 5 (idempotent)
  // ------------------------------------------------------------------
  const ignorePaymentEvent = async (eventId) => {
    try {
      await requireSession();
      if (!eventId) throw new Error('ignorePaymentEvent: missing eventId');

      // First, clear any FK references that would block the status change.
      // If this event is linked as suggested_invoice_id on an invoice, the
      // update should still work (it's the confirmed_invoice_id FK that blocks
      // deletes, not updates). But we use .select() to verify RLS allowed the write.
      const { data, error } = await supabase
        .from('bank_payment_events')
        .update({ status: 'ignored' })
        .eq('id', eventId)
        .select('id')
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error('Could not update payment event. You may not have permission — ask an admin to check the RLS policy on bank_payment_events.');

      setPaymentEvents(prev => prev.filter(e => e.id !== eventId));
      setInvoices(prev => prev.map(inv =>
        inv.paymentEvent?.id === eventId ? { ...inv, paymentDetected: false, paymentEvent: null } : inv
      ));
      return { success: true };
    } catch (err) {
      console.error('[INVOICES] ignorePaymentEvent error:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'ignore', entity: 'payment event' }) };
    }
  };

  // ------------------------------------------------------------------
  // MARK AS EXPENSE PAYMENT — for ad-hoc client payments not tied to invoices
  // ------------------------------------------------------------------
  const markAsExpensePayment = async (eventId, note = '') => {
    try {
      await requireSession();
      if (!eventId) throw new Error('markAsExpensePayment: missing eventId');

      const { data, error } = await supabase
        .from('bank_payment_events')
        .update({ status: 'expense', expense_payment: true, narration: note || undefined })
        .eq('id', eventId)
        .select('id')
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error('Could not update payment event. Check RLS policy on bank_payment_events.');

      setPaymentEvents(prev => prev.filter(e => e.id !== eventId));
      return { success: true };
    } catch (err) {
      console.error('[INVOICES] markAsExpensePayment error:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'mark expense', entity: 'payment event' }) };
    }
  };

  // ------------------------------------------------------------------
  // FETCH EXPENSE PAYMENTS — ad-hoc payments not tied to invoices
  // Returns all events where expense_payment = true, optionally filtered
  // ------------------------------------------------------------------
  const fetchExpensePayments = useCallback(async () => {
    setExpensePaymentsLoading(true);
    try {
      const { data, error } = await supabase
        .from('bank_payment_events')
        .select('*')
        .eq('expense_payment', true)
        .eq('status', 'expense')
        .order('txn_date', { ascending: false })
        .limit(200);

      if (error) throw error;

      setExpensePayments((data || []).map(r => ({
        id:               r.id,
        amount:           Number(r.amount) || 0,
        txnDate:          r.txn_date ?? null,
        payerName:        r.payer_name ?? '',
        narration:        r.narration ?? '',
        linkedProjectId:  r.linked_project_id ? normalizeId(r.linked_project_id) : null,
        expenseCategory:  r.expense_category ?? '',
        expenseOffice:    r.expense_office ?? '',
        linkedVoucherId:  r.linked_voucher_id ?? null,
        createdAt:        r.created_at ?? null,
      })));
    } catch (err) {
      console.error('[INVOICES] fetchExpensePayments error:', err);
      setExpensePayments([]);
    } finally {
      setExpensePaymentsLoading(false);
    }
  }, []);

  // ------------------------------------------------------------------
  // LINK EXPENSE PAYMENT TO PROJECT
  // ------------------------------------------------------------------
  const linkExpenseToProject = async (eventId, projectId) => {
    try {
      await requireSession();
      if (!eventId) throw new Error('linkExpenseToProject: missing eventId');

      const { error } = await supabase
        .from('bank_payment_events')
        .update({ linked_project_id: projectId || null })
        .eq('id', eventId)
        .eq('expense_payment', true);

      if (error) throw error;

      setExpensePayments(prev => prev.map(e =>
        e.id === eventId ? { ...e, linkedProjectId: projectId ? normalizeId(projectId) : null } : e
      ));
      return { success: true };
    } catch (err) {
      console.error('[INVOICES] linkExpenseToProject error:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'link expense to project', entity: 'payment event' }) };
    }
  };

  // ------------------------------------------------------------------
  // DELETE EXPENSE PAYMENT — remove ad-hoc expense from list
  // ------------------------------------------------------------------
  const deleteExpensePayment = async (eventId) => {
    try {
      await requireSession();
      if (!eventId) throw new Error('deleteExpensePayment: missing eventId');

      const { error } = await supabase
        .from('bank_payment_events')
        .delete()
        .eq('id', eventId)
        .eq('expense_payment', true);

      if (error) throw error;

      setExpensePayments(prev => prev.filter(e => e.id !== eventId));
      return { success: true };
    } catch (err) {
      console.error('[INVOICES] deleteExpensePayment error:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'delete expense payment', entity: 'payment event' }) };
    }
  };

  // ------------------------------------------------------------------
  // UPDATE EXPENSE PAYMENT — edit category, office, narration, date, project
  // ------------------------------------------------------------------
  const updateExpensePayment = async (eventId, fields) => {
    try {
      await requireSession();
      if (!eventId) throw new Error('updateExpensePayment: missing eventId');

      const dbFields = {};
      if (fields.narration !== undefined)        dbFields.narration = fields.narration;
      if (fields.txnDate !== undefined)           dbFields.txn_date = fields.txnDate;
      if (fields.expenseCategory !== undefined)   dbFields.expense_category = fields.expenseCategory;
      if (fields.expenseOffice !== undefined)     dbFields.expense_office = fields.expenseOffice;
      if (fields.linkedProjectId !== undefined)   dbFields.linked_project_id = fields.linkedProjectId || null;
      if (fields.linkedVoucherId !== undefined)  dbFields.linked_voucher_id = fields.linkedVoucherId || null;

      if (Object.keys(dbFields).length === 0) return { success: true };

      const { error } = await supabase
        .from('bank_payment_events')
        .update(dbFields)
        .eq('id', eventId)
        .eq('expense_payment', true);

      if (error) throw error;

      setExpensePayments(prev => prev.map(e => {
        if (e.id !== eventId) return e;
        const updated = { ...e };
        if (fields.narration !== undefined)        updated.narration = fields.narration;
        if (fields.txnDate !== undefined)           updated.txnDate = fields.txnDate;
        if (fields.expenseCategory !== undefined)   updated.expenseCategory = fields.expenseCategory;
        if (fields.expenseOffice !== undefined)     updated.expenseOffice = fields.expenseOffice;
        if (fields.linkedProjectId !== undefined)   updated.linkedProjectId = fields.linkedProjectId ? normalizeId(fields.linkedProjectId) : null;
        if (fields.linkedVoucherId !== undefined)   updated.linkedVoucherId = fields.linkedVoucherId || null;
        return updated;
      }));
      return { success: true };
    } catch (err) {
      console.error('[INVOICES] updateExpensePayment error:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'update expense payment', entity: 'payment event' }) };
    }
  };

  // ------------------------------------------------------------------
  // LINK PAYMENT TO INVOICE — Step 1 (for unmatched events)
  // ------------------------------------------------------------------
  const linkPaymentToInvoice = async ({ eventId, invoiceId, invoice: passedInvoice }) => {
    try {
      await requireSession();
      if (!eventId || !invoiceId) throw new Error('linkPaymentToInvoice: missing eventId or invoiceId');

      const idValue = normalizeId(invoiceId);
      const existing = passedInvoice || invoices.find(i => i.id === idValue);

      if (existing && (existing.paidConfirmed || existing.status === 'paid')) {
        return { success: false, error: 'This invoice is already confirmed as paid.' };
      }

      const { data: evtData, error: evtErr } = await supabase
        .from('bank_payment_events')
        .update({ suggested_invoice_id: idValue, status: 'suggested' })
        .eq('id', eventId)
        .select()
        .maybeSingle();

      if (evtErr) throw evtErr;
      if (!evtData) throw new Error('Payment event not found.');

      const pe = mapPaymentEvent(evtData);
      return await confirmPaymentMatch({ invoiceId, paymentEvent: pe, invoice: existing });
    } catch (err) {
      console.error('[INVOICES] linkPaymentToInvoice error:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'link payment', entity: 'invoice' }) };
    }
  };

  // ------------------------------------------------------------------
  // UNDO PAYMENT CONFIRMATION — Step 4 (admin only)
  // Reverts invoice + bank_payment_events. Does NOT erase account_name.
  // ------------------------------------------------------------------
  const undoPaymentConfirmation = async (invoiceId) => {
    try {
      await requireSession();
      if (!invoiceId) throw new Error('undoPaymentConfirmation: missing invoiceId');

      const idValue = normalizeId(invoiceId);
      const existing = invoices.find(i => i.id === idValue);

      if (!existing) return { success: false, error: 'Invoice not found in local state.' };
      if (!existing.paidConfirmed && existing.status !== 'paid') {
        return { success: false, error: 'This invoice is not marked as paid.' };
      }

      const prevPaymentEventId = existing.paymentEventId;

      const { data: invData, error: invErr } = await supabase
        .from(INVOICES_WRITE_SOURCE)
        .update({
          status: 'issued',
          paid_confirmed: false,
          paid_date: null,
          payment_event_id: null,
        })
        .eq('id', idValue)
        .is('deleted_at', null)
        .select()
        .maybeSingle();

      if (invErr) throw invErr;
      if (!invData) throw new Error('Invoice not found.');

      if (prevPaymentEventId) {
        try {
          // Check whether this event still has a suggestion to revert to;
          // if suggested_invoice_id is null, revert to 'unmatched' so the
          // inbox doesn't show a Confirm button with no invoice to confirm.
          const { data: bpeRow } = await supabase
            .from('bank_payment_events')
            .select('suggested_invoice_id')
            .eq('id', prevPaymentEventId)
            .maybeSingle();
          const revertStatus = bpeRow?.suggested_invoice_id ? 'suggested' : 'unmatched';
          await supabase
            .from('bank_payment_events')
            .update({ status: revertStatus, confirmed_invoice_id: null, confirmed_at: null })
            .eq('id', prevPaymentEventId);
        } catch (bpeErr) {
          console.warn('[INVOICES] undo: bank_payment_events revert failed (non-blocking):', bpeErr);
        }
      }

      // Re-fetch from reporting view for correct INR amounts
      const viewRow = await refetchFromView(idValue);
      setInvoices(prev => prev.map(i => i.id === idValue ? (viewRow || mapRow(invData)) : i));
      return { success: true };
    } catch (err) {
      console.error('[INVOICES] undoPaymentConfirmation error:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'undo payment', entity: 'invoice' }) };
    }
  };

  // ------------------------------------------------------------------
  // SOFT DELETE — admin only
  // Uses RPC function (admin_soft_delete_invoice) to bypass restrictive
  // UPDATE RLS policies. Falls back to direct update if RPC not found.
  // ------------------------------------------------------------------
  const deleteInvoice = async (invoiceId) => {
    try {
      await requireSession();
      if (!invoiceId) throw new Error('deleteInvoice: missing invoiceId');

      const idValue = normalizeId(invoiceId);

      // Try RPC first (bypasses UPDATE RLS restrictions)
      const { data: rpcData, error: rpcError } = await supabase
        .rpc('admin_soft_delete_invoice', { p_invoice_id: idValue });

      if (!rpcError) {
        // RPC succeeded
        setInvoices(prev => prev.filter(i => i.id !== idValue));
        setAllInvoices(prev => prev.filter(i => i.id !== idValue));
        return { success: true };
      }

      // If RPC function doesn't exist, fall back to direct update
      if (rpcError.code === '42883' || rpcError.message?.includes('function') || rpcError.message?.includes('does not exist')) {
        console.warn('[INVOICES] admin_soft_delete_invoice RPC not found, falling back to direct update');
        const now = new Date().toISOString();
        const { data, error } = await supabase
          .from(INVOICES_WRITE_SOURCE)
          .update({ deleted_at: now })
          .eq('id', idValue)
          .is('deleted_at', null)
          .select()
          .maybeSingle();

        if (error) throw error;
        if (!data) throw new Error('Invoice not found or already deleted.');

        setInvoices(prev => prev.filter(i => i.id !== idValue));
        setAllInvoices(prev => prev.filter(i => i.id !== idValue));
        return { success: true };
      }

      // RPC exists but returned an error (e.g. not admin)
      throw rpcError;
    } catch (err) {
      console.error('[INVOICES] delete error:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'invoice' }) };
    }
  };

  // ------------------------------------------------------------------
  // UPDATE STAGE KEY — admin only; for assigning stages to imported invoices
  // ------------------------------------------------------------------
  const updateInvoiceStageKey = async (invoiceId, stageKey) => {
    try {
      await requireSession();
      if (!invoiceId) throw new Error('updateInvoiceStageKey: missing invoiceId');

      const idValue = normalizeId(invoiceId);
      const { data, error } = await supabase
        .from(INVOICES_WRITE_SOURCE)
        .update({ stage_key: stageKey || null })
        .eq('id', idValue)
        .is('deleted_at', null)
        .select()
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error('Invoice not found.');

      // Only stage_key changed — merge into existing state to preserve
      // the INR-converted FX amounts from v_invoices_reporting.
      // A full refetchFromView would also work, but merging is safer
      // because it avoids losing FX data if the view query fails.
      const newStageKey = data.stage_key ?? null;
      const viewRow = await refetchFromView(idValue);
      setInvoices(prev => prev.map(i => {
        if (i.id !== idValue) return i;
        if (viewRow) return viewRow;
        // Fallback: merge stageKey into existing state (preserves FX fields)
        return { ...i, stageKey: newStageKey };
      }));
      return { success: true, data: viewRow || { ...invoices.find(i => i.id === idValue), stageKey: newStageKey } };
    } catch (err) {
      console.error('[INVOICES] updateInvoiceStageKey error:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'update stage key', entity: 'invoice' }) };
    }
  };

  // ------------------------------------------------------------------
  // BULK UPDATE STAGE KEY — admin only; batch assign stages to imported invoices
  // Uses .in('id', [...]) for a single DB round-trip
  // ------------------------------------------------------------------
  const bulkUpdateInvoiceStageKey = async (invoiceIds, stageKey) => {
    try {
      await requireSession();
      if (!invoiceIds || invoiceIds.length === 0) throw new Error('bulkUpdateInvoiceStageKey: no invoiceIds');

      const idValues = invoiceIds.map(normalizeId);
      const { data, error } = await supabase
        .from(INVOICES_WRITE_SOURCE)
        .update({ stage_key: stageKey || null })
        .in('id', idValues)
        .is('deleted_at', null)
        .select();

      if (error) throw error;

      // Only stage_key changed — merge into existing state to preserve
      // the INR-converted FX amounts from v_invoices_reporting.
      const newStageKey = stageKey || null;
      const idSet = new Set(idValues);
      const viewMap = await refetchManyFromView(idValues);
      setInvoices(prev => prev.map(i => {
        if (!idSet.has(i.id)) return i;
        if (viewMap && viewMap.has(i.id)) return viewMap.get(i.id);
        // Fallback: merge stageKey into existing state (preserves FX fields)
        return { ...i, stageKey: newStageKey };
      }));
      return { success: true, count: idSet.size };
    } catch (err) {
      console.error('[INVOICES] bulkUpdateInvoiceStageKey error:', err);
      return { success: false, error: formatSupabaseError(err, { action: 'bulk update stage key', entity: 'invoices' }) };
    }
  };

  // ------------------------------------------------------------------
  // RETURN
  // ------------------------------------------------------------------
  return {
    invoices,
    allInvoices,
    loading,
    fetchInvoices,
    fetchAllInvoices,
    refreshInvoices: fetchAllInvoices,
    fetchInvoicesByStage,
    fetchInvoiceSummaryForProject,
    allocateInvoiceNumber,
    createInvoice,
    updateInvoice,
    issueInvoice,
    confirmPayment,
    confirmPaymentMatch,
    upsertProjectAccountName,
    fetchProjectAccountNames,
    deleteInvoice,
    // Stage assignment for imported invoices
    updateInvoiceStageKey,
    bulkUpdateInvoiceStageKey,
    // Payment events — Step 1
    paymentEvents,
    paymentEventsLoading,
    suggestedPaymentEvents,
    unmatchedPaymentEvents,
    pendingPaymentCount,
    fetchPendingPaymentCount,
    fetchPaymentEvents,
    refreshPaymentEvents,
    ignorePaymentEvent,
    markAsExpensePayment,
    linkPaymentToInvoice,
    // Expense payments — Project Expenses page
    expensePayments,
    expensePaymentsLoading,
    fetchExpensePayments,
    linkExpenseToProject,
    updateExpensePayment,
    deleteExpensePayment,
    // Archived (confirmed) payment events — paginated
    archivedPaymentEvents,
    archivedPaymentEventsLoading,
    archivedPaymentPage,
    archivedPaymentHasNext,
    fetchArchivedPaymentEvents,
    // Step 4 — undo
    undoPaymentConfirmation,
    // Recalculation helpers
    getIssuedInvoices,
    getIssuedInvoicesForStage,
    getInvoicedBaseForStage,
    getLastIssuedDate,
    refetch: fetchInvoices,
  };
};
